import { GridColDef, GridRowsProp } from '@mui/x-data-grid';
import { useContext, useEffect, useState } from 'react';
import { AppContext } from '../../../app-context';
import IGridComponent from '../../../components/grid/i-grid.component';
import TitlePanel from '../../../components/title-panel/title-panel.component';
import { ALL, DAYS_MONTH, PAGE_SIZE } from '../../common/constants';
import { formatDate, formatTo0Decimal, formatTo1Decimal, formatTo2Decimal, isEmpty, isEmptyArray, isNotEmpty } from '../../common/util.services';
import { getAllProducts, getSkuAdSpendData, saveAdProduct, splitVirtualProduct, syncAdSkuAttribute} from '../amazon.services';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import '../amazon.scss';
import SearchPanel from '../../../components/search-panel/search-panel.component';
import IDropdown from '../../../components/text-component/dropdown/i-dropdown.component';
import { Grid, Stack } from '@mui/material';
import IDateField from '../../../components/text-component/i-datefield/i-datefield.component';
import ICheckbox from '../../../components/checkbox/i-checkbox.component';
import ITextField from '../../../components/text-component/textfield/i-textfield.component';
import CallSplitIcon from '@mui/icons-material/CallSplit';
import YesNoDialog from '../../../components/message-dialog/yes-no-dialog.component';

const AmazonAdSpendSku = () => {
  const appContext = useContext(AppContext);
  const rows: GridRowsProp = [];
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const [adSpendData, setAdSpendData] = useState([{rowId: '', date: 'Loading...'}]);
  const [products, setProducts] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [showSplitMsg, setShowSplitMsg] = useState(false);
  const [cellRowValue, setCellRowValue] = useState();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [totalRows, setTotalRows] = useState(0);
  const [searchData, setSearchData] = useState<any>({adType: ALL, sort: 'Date', sortType: 'Descending', noAttribute: false, 
                                                     virtual:false, noPrd: false, dateRange: ALL, product: ALL, sku: ALL, adName: ALL,
                                                     startDate: dayjs().subtract(DAYS_MONTH, 'day'), endDate: dayjs()});
  const columns: GridColDef[] = [
    { field: 'date', headerName: 'Date', filterable: false, sortable: false, editable: false, width: 100,
      valueFormatter: (value: any) => isEmpty(value) ? 'Total' : 'Loading...' === value ? 'Loading...' : formatDate(value)
    },
    { field: 'product', headerName: 'Product', filterable: false, sortable: false, editable: true, width: 130, type: 'singleSelect',
      valueOptions: products,
      valueFormatter: (product: any) => product, 
      valueSetter: (value, row) => { row.product = value; return value; }
    },
    { field: 'sku', headerName: 'SKU', filterable: false, sortable: false, editable: false, width: 90,
      renderCell: (params) => (showIcon(params.row) ? <Stack direction="row" alignItems="center" gap={1}>
                                  <CallSplitIcon fontSize='small' color="primary"/>
                                  {params.row.sku}</Stack> : params.row.sku)
    },
    { field: 'color', headerName: 'Color', filterable: false, sortable: false, editable: false, width: 115 },
    { field: 'size', headerName: 'Size', filterable: false, sortable: false, editable: false, width: 70 },
    { field: 'unitCount', headerName: 'Pack', filterable: false, sortable: false, editable: false, width: 60,
      valueFormatter: (value: any) => -1 === value ? 'Total' : 0 === value ? 'Not Defined' : -2 === value ? 'NA' : value
    },
    { field: 'adType', headerName: 'Ad Type', filterable: false, sortable: false, editable: false, width: 80 },
    { field: 'impression', headerName: 'Impression', filterable: false, sortable: false, editable: false, width: 95,
      valueFormatter: (value: any) => formatTo0Decimal(value)
    },
    { field: 'ctr', headerName: 'CTR', filterable: false, sortable: false, editable: false, width: 65,
      valueFormatter: (value: any) => formatTo2Decimal(value) + '%'
    },
    { field: 'click', headerName: 'Click', filterable: false, sortable: false, editable: false, width: 65,
      valueFormatter: (value: any) => formatTo0Decimal(value)
    },
    { field: 'spend', headerName: 'Spend', filterable: false, sortable: false, editable: false, width: 75,
      valueFormatter: (value: any) => formatTo0Decimal(value)
    },
    { field: 'cpc', headerName: 'CPC', filterable: false, sortable: false, editable: false, width: 50,
      valueFormatter: (value: any) => isEmpty(value) ? 'NA' : formatTo2Decimal(value)
    },
    { field: 'sale', headerName: 'Ad Sale', filterable: false, sortable: false, editable: false, width: 85,
      valueFormatter: (value: any) => formatTo0Decimal(value)
    },
    { field: 'totalSale', headerName: 'Total Sale', filterable: false, sortable: false, editable: false, width: 85,
      valueFormatter: (value: any) => formatTo0Decimal(value)
    },
    { field: 'acos', headerName: 'ACOS', filterable: false, sortable: false, editable: false, width: 70,
      cellClassName: (params) => { return getClassNameAcos(params.row.acos) },
      valueFormatter: (value: any) => isEmpty(value) ? 'NA' : formatTo1Decimal(value) + '%'
    },
    { field: 'tacos', headerName: 'TACOS', filterable: false, sortable: false, editable: false, width: 70,
      cellClassName: (params) => { return getClassNameTacos(params.row.tacos) },
      valueFormatter: (value: any) => isEmpty(value) ? 'NA' : formatTo1Decimal(value) + '%'
    },
    { field: 'adName', headerName: 'Ad Name', filterable: false, sortable: false, editable: false, width: 500 }
  ];

  useEffect(() => {
    getAllProducts(appContext,
      (response: any) => {
        setProducts(response.data);
        appContext.setShowLoader(false);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message);
      });
  }, []);

  useEffect(() => {
    getAdSpendData();
  }, [searchData, page, pageSize]);

  function showIcon(row: any) {
    return isEmpty(row.product) && isNotEmpty(row.sku) && 'NA' !== row.sku && 'Total' !== row.sku;
  }

  function getAdSpendData() {
    if(isEmptyArray(adSpendData)) {
      setAdSpendData([{rowId: '', date: 'Loading...'}]);
    }
    if (isEmptyArray(rows)) {
      setLoading(true);
      appContext.setShowLoader(true);
      getSkuAdSpendData(searchData, page, pageSize, appContext,
        (response: any) => {
          setAdSpendData(response.data.content);
          setTotalRows(response.data.totalElements);
          appContext.setShowLoader(false);
          setLoading(false);
        }, (error: any) => {
          console.log(error);
          appContext.setShowLoader(false);
          appContext.setErrorMessage(error.message);
          setLoading(false);
        });
    }
  }

  const syncSkuData = () => {
    appContext.setShowLoader(true);
    setLoading(true);
    syncAdSkuAttribute(appContext,
      (response: any) => {
        getAdSpendData();
        appContext.setShowLoader(false);
        setLoading(false);
      }, (error: any) => {
        console.log(error);
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message);
        setLoading(false);
      });
  }

  const onPaginationModelChange = (evt: any) => {
    setPage(evt.page);
    setPageSize(evt.pageSize);
  }

  function getClassNameTacos(value: any) {
    return isEmpty(value) ? '' : value > 20 ? "PerfNegative" : "PerfPositive";
  }

  function getClassNameAcos(value: any) {
    return isEmpty(value) ? '' : value > 35 ? "PerfNegative" : "PerfPositive";
  }

  const search = (e: any) => {
    setSearchData(e);
  }

  const onCellClick = (value: any) => {
    const field = value.field;
    if('sku' === field && showIcon(value.row)) {
      setCellRowValue(value.row);
      setShowSplitMsg(true);
    }
  }

  const onOkClick = () => {
    setLoading(true);
    appContext.setShowLoader(true);
    splitVirtualProduct(cellRowValue, appContext,
      (response: any) => {
        appContext.setSuccessMessage(response.message)
        getAdSpendData();
        appContext.setShowLoader(false);
        setLoading(false);
      }, (error: any) => {
        appContext.setShowLoader(false);
        appContext.setErrorMessage(error.message)
        setLoading(false);
    });
  }

  const processRowUpdate = (updatedRow: any, originalRow: any) => {
    if(ALL === searchData.dateRange && ALL === searchData.product && ALL === searchData.sku &&
       ALL === searchData.adName && ALL === searchData.adType) {
      setLoading(true);
      appContext.setShowLoader(true);
      saveAdProduct(originalRow, appContext,
        (response: any) => {
          appContext.setSuccessMessage(response.message)
          getAdSpendData();
          appContext.setShowLoader(false);
          setLoading(false);
        }, (error: any) => {
          appContext.setShowLoader(false);
          appContext.setErrorMessage(error.message)
          setLoading(false);
      });
    } else {
      appContext.setDialogMessage('Product update is applicable only for \'All\' parameters.');
    }
  }

  const getSearchComponent = () => {
    return <SearchPanel search={search} formData={searchData}>
      <Grid container spacing={1} alignItems="center">
        <Grid xs={2}>
          <IDropdown name='dateRange' label='Date' allItemRequired={false} required={true} itemList={['All', 'Total']} />
        </Grid>
        <Grid xs={2}>
          <IDropdown name='product' label='Product' allItemRequired={true} required={true} itemList={['Total', ...products]} />
        </Grid>
        <Grid xs={2}>
          <IDropdown name='sku' label='SKU' allItemRequired={false} required={true} itemList={['All', 'Total']} />
        </Grid>
        <Grid xs={2}>
          <IDropdown name='adName' label='Ad Name' allItemRequired={false} required={true} itemList={['All', 'Total']} />
        </Grid>
        <Grid xs={2}>
          <IDropdown name='adType' label='Ad Type' allItemRequired={true} required={true}
            itemList={['Total', 'Sponsored Brands', 'Sponsored Display', 'Sponsored Products', 'Full Funnel']} />
        </Grid>
        <Grid xs={2}>
          <ITextField name='searchText' label='SKU/Ad Name'/>
        </Grid>
        <Grid xs={2}>
          <IDateField name='startDate' label='Start Date' timeZone='America/Los_Angeles'/>
        </Grid>
        <Grid xs={2}>
          <IDateField name='endDate' label='End Date' timeZone='America/Los_Angeles'/>
        </Grid>
        <Grid xs={2}>
          <IDropdown name='sort' label='Sort' required={true} allItemRequired={false}
            itemList={['ACOS', 'Ad Name',  'Click', 'CTR', 'Date', 'Impression', 'Product', 'SKU', 'Spend']} />
        </Grid>
        <Grid xs={2}>
          <IDropdown name='sortType' label='Sort Type' itemList={['Ascending', 'Descending']} required={true} allItemRequired={false}/>
        </Grid>
        <Grid xs={1.33}>
          <ICheckbox name='virtual' label='Virtual' defaultChecked={false} />
        </Grid>
        <Grid xs={1.33}>
          <ICheckbox name='noPrd' label='No Product' defaultChecked={false} />
        </Grid>
        <Grid xs={1.33}>
          <ICheckbox name='noAttribute' label='No Attribute' defaultChecked={false} />
        </Grid>
      </Grid>
    </SearchPanel>
  }

  const getMainPanel = () => {
    return <>
      {getSearchComponent()}
      <IGridComponent title={''} rows={adSpendData} columns={columns} loading={loading} maxHeight={450}
          paginationType="server" paginationModelChange={onPaginationModelChange} totalRows={totalRows}
          getRowId={(row: any) => row.rowId} processRowUpdate={processRowUpdate} cellClick={onCellClick}
          showExtBtn={true} extBtnText={'Sync SKU Attribute'} extBtnAction={syncSkuData}
          showAction={false} showApproveAction={false} showDeleteAction={false} showEditAction={false} />
      <YesNoDialog open={showSplitMsg} onClose={() => { setShowSplitMsg(false) }}
                onOk={() => { setShowSplitMsg(false); onOkClick(); }} isHorizontal={true} 
                message={'Do you want to split the data?'}/>
    </>
  }

  return <TitlePanel title={'Ad Spend'} panelBody={getMainPanel()} />
}

export default AmazonAdSpendSku;